import { ref } from '@nuxtjs/composition-api'
import { voucherDebugError } from './useVoucherDebug'
import { useVoucherErrorHandling } from './useVoucherErrorHandling'
import { useVoucherHelpers } from './useVoucherHelpers'
import { ACTION_STATUS } from '~/constants/ActionStatus'
import { useCartStore } from '~/stores/useCartStore'

/**
 * Composable for voucher application and removal
 * Handles applying and removing voucher codes, error handling, and state management
 *
 * Phase 1: Extracted from VoucherCode.vue to improve separation of concerns
 */
export const useVoucherApplication = (context) => {
  const { $hubble } = context
  const cartStore = useCartStore()
  const { getCartID, getProductInfo } = useVoucherHelpers()
  const { isCartStateError, getErrorMessage } = useVoucherErrorHandling()

  // State
  const loading = ref(false)
  const removing = ref(false)
  const errorMessage = ref('')
  const infoMessage = ref('')
  const isApplied = ref(false)


  const processResponse = (response, onSuccess, onError) => {
    loading.value = false
    infoMessage.value = ''

    const data = response?.data
    if (data?.actionStatus === ACTION_STATUS.OK && data?.responseObject) {
      onSuccess(data.responseObject)
    } else {
      const error = data?.actionErrors?.[0]
      const errorMsg = error?.notificationMessage || data?.actionMessage || 'Ugyldig rabatkode'
      onError(errorMsg)
    }
  }

  const validateApplyVoucherInputs = (voucherCode) => {
    if (!voucherCode || loading.value || isApplied.value) {
      return false
    }
    return true
  }

  const buildApplyVoucherRequest = (voucherCode) => {
    const { sku, channelKey, installmentPeriod } = getProductInfo()
    return {
      discountCode: voucherCode,
      sku,
      channelKey,
      installmentPeriod
    }
  }

  const handleApplyVoucherError = (error, onError) => {
    voucherDebugError('Discount code error:', error)
    loading.value = false
    infoMessage.value = ''

    if (isCartStateError(error)) {
      cartStore.setCart(null)
    }

    const errorMsg = getErrorMessage(error)
    onError(errorMsg)
  }

  // Apply voucher
  const applyVoucher = async (voucherCode, onSuccess, onError) => {
    try {
      if (!validateApplyVoucherInputs(voucherCode)) {
        return
      }

      const cartID = getCartID()
      if (!cartID) {
        onError('Ingen indkøbskurv fundet. Vent venligst.')
        return
      }

      loading.value = true
      errorMessage.value = ''
      infoMessage.value = 'Vent venligst, mens vi anvender din rabatkode'

      const request = buildApplyVoucherRequest(voucherCode)
      const response = await $hubble.cart.applyDiscountCodeWithProduct(cartID, request)
      processResponse(response, onSuccess, onError)
    } catch (error) {
      handleApplyVoucherError(error, onError)
    }
  }

  const processRemoveVoucherResponse = (response, onSuccess, onError) => {
    removing.value = false
    const data = response?.data

    if (data?.actionStatus === ACTION_STATUS.OK && data?.responseObject) {
      onSuccess(data.responseObject)
    } else {
      const error = data?.actionErrors?.[0]
      const errorMsg = error?.notificationMessage || data?.actionMessage || 'Kunne ikke fjerne rabatkoden'
      onError(errorMsg)
    }
  }

  const handleRemoveVoucherError = (error, onError) => {
    voucherDebugError('Remove discount code error:', error)
    removing.value = false
    const errorMsg = getErrorMessage(error)
    onError(errorMsg)
  }

  // Remove voucher
  const removeVoucher = async (discountCodeId, onSuccess, onError) => {
    try {
      if (!discountCodeId) {
        onError('Ingen rabatkode at fjerne')
        return
      }

      removing.value = true
      errorMessage.value = ''

      const cartID = getCartID()
      if (!cartID) {
        onError('Ingen indkøbskurv fundet. Vent venligst.')
        return
      }

      const response = await $hubble.cart.removeDiscountCode(cartID, discountCodeId)
      processRemoveVoucherResponse(response, onSuccess, onError)
    } catch (error) {
      handleRemoveVoucherError(error, onError)
    }
  }

  return {
    // State
    loading,
    removing,
    errorMessage,
    infoMessage,
    isApplied,

    // Methods
    applyVoucher,
    removeVoucher
  }
}
